import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, Collapse, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Notification } from '@eprintsmart/shared';
import { DAILY_SEND_LIMIT, resendNotification, watchNotifications } from '../services/notifications';
import { ParkedReplies } from '../components/ParkedReplies';

/**
 * Everything the site has tried to send.
 *
 * This page exists because of how the last failure was found. The queue behind
 * it was written in July, filled up, and was never drained — the transport it
 * assumed had simply never been built — and nothing anywhere showed that. Order
 * confirmations silently went nowhere for weeks while checkout kept promising
 * them.
 *
 * So the point of this page is not the table. It is the counts at the top: if
 * mail stops going out again, the number that matters is visible without
 * knowing to come and look for it.
 */

const RELATIVE = (ms: number) => {
  const mins = Math.floor((Date.now() - ms) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const STATE_COLOUR = {
  SENT: 'success',
  PENDING: 'default',
  FAILED: 'error',
} as const;

const KIND_LABEL: Record<string, string> = {
  ORDER_STATUS: 'Order',
  PAYMENT_REMINDER: 'Payment reminder',
  ORDER_INTERNAL: 'New order (internal)',
  PAYMENT_ALERT: 'Payment needs a person',
  REVIEW_REQUEST: 'Review request',
  VENDOR_INVITE: 'Vendor invite',
  VENDOR_ASSIGNED: 'Vendor job',
  ARTIST_INVITE: 'Artist invite',
  ARTIST_APPLIED: 'Artist applied',
  ARTIST_REJECTED: 'Artist declined',
  ARTIST_WELCOME: 'Artist welcome',
  ARTIST_MONTHLY: 'Artist monthly',
  ARTIST_REFERRAL: 'Artist referral',
  ARTIST_SOLD: 'Artist sale',
  ARTIST_PAID: 'Artist payout',
  GRIEVANCE_ACK: 'Grievance ack',
  COMMISSION_ENQUIRY: 'New brief',
  COMMISSION_ACCEPTED: 'Brief accepted',
  COMMISSION_DECLINED: 'Brief declined',
  COMMISSION_DELIVERED: 'Work delivered',
  COMMISSION_REVISION: 'Change requested',
  COMMISSION_APPROVED: 'Work approved',
  COMMISSION_CANCELLED: 'Commission cancelled',
  COMMISSION_MESSAGE: 'Commission message',
};

export function Notifications() {
  const [rows, setRows] = useState<Notification[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => watchNotifications(setRows), []);

  const counts = useMemo(() => {
    const dayAgo = Date.now() - 86_400_000;
    return {
      failed: rows.filter((r) => r.deliveryState === 'FAILED').length,
      pending: rows.filter((r) => r.deliveryState === 'PENDING').length,
      sentToday: rows.filter((r) => r.deliveryState === 'SENT' && (r.sentAt ?? 0) > dayAgo).length,
    };
  }, [rows]);

  const resend = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      const result = await resendNotification(id);
      if (result?.deliveryState === 'FAILED') {
        setError(`Still failing: ${result.lastError ?? 'no reason given'}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend that.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Above the outbound log on purpose: a parked reply is a customer waiting
          for an answer, and the log below it is history. It renders nothing at
          all when there is nothing waiting. */}
      <ParkedReplies />

      {counts.failed > 0 && (
        <Alert severity="error">
          {counts.failed} message{counts.failed === 1 ? '' : 's'} could not be delivered after five
          attempts. Open the row to see why — a bad key or a revoked delegation fails every message,
          not just one.
        </Alert>
      )}

      <Box>
        <Typography variant="h6" gutterBottom>Outbound email</Typography>
        <Stack direction="row" spacing={3} mb={2}>
          <Box>
            <Typography variant="h4">{counts.sentToday}</Typography>
            <Typography variant="caption" color="text.secondary">
              sent in 24h &middot; cap {DAILY_SEND_LIMIT.toLocaleString('en-IN')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color={counts.pending > 20 ? 'warning.main' : 'text.primary'}>
              {counts.pending}
            </Typography>
            <Typography variant="caption" color="text.secondary">waiting</Typography>
          </Box>
          <Box>
            <Typography variant="h4" color={counts.failed ? 'error.main' : 'text.primary'}>
              {counts.failed}
            </Typography>
            <Typography variant="caption" color="text.secondary">failed</Typography>
          </Box>
        </Stack>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Sent from barath@erevu.in, a mailbox shared with OfficeSmart — the daily cap counts
          both. Anything still waiting is retried every fifteen minutes, five times, before it is
          given up on.
        </Typography>

        <Paper variant="outlined">
          {rows.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                Nothing queued yet.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Kind</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell align="right">State</TableCell>
                  <TableCell align="right" width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small"
                          onClick={() => setOpen(open === row.id ? null : row.id)}>
                          <ExpandMoreIcon fontSize="small"
                            sx={{ transform: open === row.id ? 'rotate(180deg)' : 'none' }} />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={KIND_LABEL[row.kind] ?? row.kind} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.to?.email ?? '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" noWrap>{row.subject}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={new Date(row.createdAt).toLocaleString('en-IN')}>
                          <Typography variant="caption" color="text.secondary">
                            {RELATIVE(row.createdAt)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small" color={STATE_COLOUR[row.deliveryState] ?? 'default'}
                          label={
                            row.deliveryState === 'PENDING' && row.attempts > 0
                              ? `retrying (${row.attempts})`
                              : row.deliveryState.toLowerCase()
                          } />
                      </TableCell>
                      <TableCell align="right">
                        {row.deliveryState !== 'SENT' && (
                          <Tooltip title="Try again now">
                            <span>
                              <IconButton size="small" disabled={busy === row.id}
                                onClick={() => void resend(row.id)}>
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open === row.id} unmountOnExit>
                          <Box py={2} px={1}>
                            {row.lastError && (
                              <Alert severity="warning" sx={{ mb: 2 }}>
                                {row.lastError}
                              </Alert>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {row.id}
                              {row.providerMessageId && ` · gmail ${row.providerMessageId}`}
                              {row.attempts > 0 && ` · ${row.attempts} attempt${row.attempts === 1 ? '' : 's'}`}
                            </Typography>
                            {/* The body as it was queued. Being able to read the
                                exact words sent is the point of keeping a queue
                                at all — it is the answer to "you never told me". */}
                            <Typography variant="body2" component="pre"
                              sx={{
                                mt: 1, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                                bgcolor: 'action.hover', p: 1.5, borderRadius: 1,
                              }}>
                              {row.body}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}
